<template>
  <footer class="bg-[#001970] text-white flex flex-col px-6 py-4">
    <!-- 顶部区域：左（Logo + Follow Us），右（Menu） -->
    <div class="w-full flex justify-between items-start">
      <!-- 左侧：Logo + Follow Us -->
      <div class="flex flex-col items-start">
        <!-- Logo -->
        <div class="flex items-center mb-6">
          <img src="../assets/logo2.png" alt="Logo" class="h-8 mr-2" />
        </div>

        <!-- Follow Us -->
        <div class="flex flex-col items-start mt-8">
          <p class="text-base font-normal">{{ footerTexts.followUs }} :</p>
          <div class="flex gap-4 mt-3">
            <a v-for="(icon, index) in icons" :key="index" @click="handleSocialClick(icon)" class="cursor-pointer">
              <img :src="icon.src" :alt="icon.alt" class="h-6 w-6" />
            </a>
          </div>
        </div>
      </div>

      <!-- 右侧：菜单 -->
      <ul class="flex flex-col items-end space-y-4 font-bold text-base max-w-[50%]">
        <li v-for="item in menuItems" :key="item.path"
            :id="item.labelKey === 'menu.contactUs' ? 'cta-contact-footer' : undefined"
            @click="() => handleMenuClick(item)"
            :class="route.path === item.path ? 'text-[#FFA200]' : ''"
            class="cursor-pointer text-right break-words leading-tight">
          {{ getMenuText(item.labelKey) }}
        </li>
      </ul>
    </div>

    <!-- 底部版权 -->
    <div class="w-full border-t border-white mt-4 pt-3 text-center text-sm font-normal">
      {{ footerTexts.copyright }}
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed } from "vue";
import { useI18n } from 'vue-i18n';
import { pushToDataLayer } from '../utils/gtm';
import { icons } from "../utils/imageData";
import { saveCTAInfo, CTA_CONFIG } from '../utils/ctaTracking';

const { locale, messages } = useI18n();
const route = useRoute();
const router = useRouter();

const footerTexts = computed(() => {
  return messages.value[locale.value]?.footer || {
    copyright: '© 2024 All rights reserved',
    followUs: 'Follow Us'
  };
});

const menuTexts = computed(() => {
  return messages.value[locale.value]?.menu || {
    home: 'Home',
    ourOffers: 'Our Offers',
    aboutUs: 'About Us',
    contactUs: 'Contact Us'
  };
});

const getMenuText = (labelKey: string) => {
  if (labelKey === 'menu.home') return menuTexts.value.home;
  if (labelKey === 'menu.ourOffers') return menuTexts.value.ourOffers;
  if (labelKey === 'menu.aboutUs') return menuTexts.value.aboutUs;
  if (labelKey === 'menu.contactUs') return menuTexts.value.contactUs;
  if (labelKey === 'footer.privacyPolicy') return footerTexts.value.privacyPolicy;
  return labelKey;
};

const menuItems = [
  { labelKey: "menu.home", path: "/" },
  { labelKey: "menu.ourOffers", path: "/core-offers" },
  { labelKey: "menu.aboutUs", path: "/about" },
  { labelKey: "menu.contactUs", path: "/contact-us" },
  { labelKey: "footer.privacyPolicy", path: "/privacy-policy" },
];


const navigate = (path: string) => {
  if (route.path !== path) {
    router.push(path).then(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  } else {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  pushToDataLayer('click', {
    category: 'footer',
    label: path,
  });
};

// 处理菜单项点击，Contact Us 需要保存 CTA 信息
const handleMenuClick = (item: { labelKey: string; path: string }) => {
  if (item.labelKey === 'menu.contactUs') {
    saveCTAInfo(CTA_CONFIG.FOOTER_CONTACT);
  }
  navigate(item.path);
};

const handleSocialClick = (icon: any) => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  
  if (icon.alt === 'LinkedIn') {
    if (isIOS) {
      // 尝试打开LinkedIn app，失败则跳转App Store
      window.location.href = 'linkedin://profile/company/107718920';
      setTimeout(() => {
        window.location.href = 'https://apps.apple.com/app/linkedin/id288429040';
      }, 1000);
    } else if (isAndroid) {
      window.location.href = 'intent://profile/company/107718920#Intent;package=com.linkedin.android;scheme=linkedin;launchFlags=0x14000000;end';
    } else {
      window.open(icon.url, '_blank');
    }
  } else if (icon.alt === 'Facebook') {
    if (isIOS) {
      window.location.href = 'fb://profile/61575843466910';
      setTimeout(() => {
        window.location.href = 'https://apps.apple.com/app/facebook/id284882215';
      }, 1000);
    } else if (isAndroid) {
      window.location.href = 'intent://profile/61575843466910#Intent;package=com.facebook.katana;scheme=fb;launchFlags=0x14000000;end';
    } else {
      window.open(icon.url, '_blank');
    }
  } else if (icon.alt === 'X') {
    if (isIOS) {
      window.location.href = 'twitter://user?screen_name=CrestonGroupGL';
      setTimeout(() => {
        window.location.href = 'https://apps.apple.com/app/x/id333903271';
      }, 1000);
    } else if (isAndroid) {
      window.location.href = 'intent://user?screen_name=CrestonGroupGL#Intent;package=com.twitter.android;scheme=twitter;launchFlags=0x14000000;end';
    } else {
      window.open(icon.url, '_blank');
    }
  }

  pushToDataLayer('click', {
    category: 'social_media',
    label: icon.alt,
  });
};
</script>
